import { callZohoAPI } from '../utils/api'
import { formatZohoDateTime } from '../utils/api'
import { errorLogService } from './errorLog'
import { filterContactsClientSide } from './crmApiHelper'

// ── Types ──

export interface CrmTag {
  id: string
  Name: string
  Team?: string
  Region?: string
  Owner?: { id: string; name: string }
  Created_Time?: string
  Modified_Time?: string
}

export interface CrmCategory {
  id: string
  Name: string
  Description?: string
  Team?: string
  Region?: string
  Owner?: { id: string; name: string }
  Created_Time?: string
  Modified_Time?: string
}

export interface CrmMailingList {
  id: string
  Name: string
  Description?: string
  Status?: string
  Subscriber_Count?: number
  Intake_Limit?: number
  Paused_Message?: string
  Closed_Message?: string
  Team?: string
  Region?: string
  Sub_Region?: string
  Created_Time?: string
  Modified_Time?: string
}

export interface CrmContact {
  id: string
  Email: string
  First_Name?: string
  Last_Name?: string
  Full_Name?: string
  Mailing_List?: string
  Tags?: string
  Categories?: string
  Region?: string
  Sub_Region?: string
  Team?: string
  Bucket_ID?: string
  Bucket_ID_Expires_At?: string
  Created_Time?: string
  Modified_Time?: string
  Owner?: { name: string; id: string; email: string }
  Title?: string
  Department?: string
  Phone?: string
  Mobile?: string
  Status?: string
  Form_Name?: string
  Form_Payload?: string
}

export interface BucketAssignProgress {
  totalContacts: number
  processedContacts: number
  currentBatch: number
  totalBatches: number
  updatedCount: number
  failedCount: number
}

// ── Helpers ──

/**
 * Fields that can be fetched inline via the `fields` parameter.
 * NOTE: Mailing_List and Categories are MULTISELECT LOOKUP fields —
 * they CANNOT be fetched inline. They must be fetched via Related Records API.
 */
const CONTACT_FIELDS = [
  'id',
  'Email',
  'First_Name',
  'Last_Name',
  'Full_Name',
  'Tags',
  'Region',
  'Sub_Region',
  'Team',
  'Bucket_ID',
  'Bucket_ID_Expires_At',
  'Status',
  'Form_Name',
  'Form_Payload',
  'Phone',
  'Mobile',
  'Created_Time',
  'Modified_Time',
  'Owner',
  'Title',
  'Department',
].join(',')

/**
 * Normalize a contact record from the Zoho API.
 * Multiselect lookup fields (Mailing_List, Categories) may come back as:
 *   - An array of objects: [{name: "Test 2", id: "123"}, ...]
 *   - A comma-separated string: "Test 2, Test 1"
 *   - null/undefined
 * This function normalizes them to comma-separated strings for consistent handling.
 */
function normalizeContact(contact: any): CrmContact {
  const normalized = { ...contact }

  // Normalize multiselect lookup fields from array format to comma-separated string
  for (const field of ['Mailing_List', 'Categories']) {
    const value = normalized[field]
    if (Array.isArray(value)) {
      normalized[field] = value
        .map((item: any) => {
          if (typeof item === 'string') return item
          return item?.name || item?.Name || item?.display_value || ''
        })
        .filter(Boolean)
        .join(', ')
    }
    // If it's an object with a name (single lookup), convert to string
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      normalized[field] = value.name || value.Name || ''
    }
  }

  return normalized as CrmContact
}

function logAndThrow(action: string, err: unknown, payload?: any): never {
  const message = err instanceof Error ? err.message : 'Unknown error'
  const debug = (err as any)?.debug
  errorLogService.log(action, message, {
    ...(payload ? { sentPayload: payload } : {}),
    ...(debug ? { debug } : {}),
  })
  throw err
}

function isNoDataError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return (
    msg.includes('NO_CONTENT') ||
    msg.includes('204') ||
    msg.includes('no data') ||
    msg.includes('No Content')
  )
}

function handleCrmListResponse<T>(response: any): T[] {
  if (!response) return []
  if (response.data && Array.isArray(response.data)) return response.data
  return []
}

function hasMoreRecords(response: any): boolean {
  return response?.info?.more_records === true
}

// ── Related List API Names ──
// The Zoho CRM Related Records API requires the EXACT related list API name,
// NOT the module name. These are confirmed from CRM Settings > API Names:
//   Mailing Lists → Contacts = "Contacts5"
//   Categories → Contacts = "Contacts4"
//   Contacts → Mailing Lists = "Mailing_Lists3"
//   Contacts → Categories = "Categories14"

const RELATED_LIST_NAMES: Record<string, string> = {
  'Mailing_Lists:Contacts': 'Contacts5',
  'Categories:Contacts': 'Contacts4',
  'Contacts:Mailing_Lists': 'Mailing_Lists3',
  'Contacts:Categories': 'Categories14',
}

// ── API Version ──
// Related Records API and Settings API require v8.
// See: https://www.zoho.com/crm/developer/docs/api/v8/get-related-records.html
// URL pattern: {api-domain}/crm/v8/{module_api_name}/{record_id}/{related_list_api_name}
const CRM_VERSION = 'v8'

/**
 * Get the correct related list API name for a parent→child module relationship.
 * Uses hardcoded known names (most reliable). Falls back to v8 Settings API discovery.
 */
async function getRelatedListApiName(
  parentModule: string,
  childModule: string,
): Promise<string> {
  const key = `${parentModule}:${childModule}`

  // Use known hardcoded names first (most reliable)
  if (RELATED_LIST_NAMES[key]) {
    console.log(
      `Using known related list API name: ${key} → "${RELATED_LIST_NAMES[key]}"`,
    )
    return RELATED_LIST_NAMES[key]
  }

  // Fallback: discover via Settings API (v8)
  console.log(`No known API name for ${key}, discovering via Settings API...`)
  return discoverRelatedListName(parentModule, childModule)
}

// ── Related List Discovery ──
// The Zoho CRM Related Records API requires the EXACT related list API name,
// NOT the module name. E.g., the Contacts relation on Mailing_Lists might be
// "Contacts3" not "Contacts". We must discover this via the Settings API.

/**
 * Cache for discovered related list API names.
 * Key: "parentModule:childModule" → Value: related list api_name
 */
const relatedListNameCache: Record<string, string> = {}

/**
 * Discover the correct related list API name for a child module on a parent module.
 *
 * Calls: GET /crm/v8/settings/related_lists?module={parentModule}
 * Then finds the entry whose api_name or display name matches the childModule.
 */
async function discoverRelatedListName(
  parentModule: string,
  childModuleApiName: string,
): Promise<string> {
  const cacheKey = `${parentModule}:${childModuleApiName}`

  if (relatedListNameCache[cacheKey]) {
    return relatedListNameCache[cacheKey]
  }

  console.log(
    `Discovering related list name: ${parentModule} → ${childModuleApiName} (using v8 API)...`,
  )

  try {
    // Use v8 Settings API — v2 doesn't return module.api_name for custom modules
    const response = await callZohoAPI(
      `crm/${CRM_VERSION}/settings/related_lists?module=${parentModule}`,
    )

    const relatedLists = response?.related_lists || []

    // Strategy 1: Match by module.api_name (works in v8)
    let matches = relatedLists.filter(
      (rl: any) => rl.module?.api_name === childModuleApiName,
    )

    // Strategy 2: If no module.api_name match, match by display name
    // e.g., related list with name "Contacts" and api_name "Contacts5"
    if (matches.length === 0) {
      matches = relatedLists.filter(
        (rl: any) =>
          rl.name === childModuleApiName ||
          rl.name === childModuleApiName.replace(/_/g, ' '),
      )
    }

    // Strategy 3: Match by api_name starting with the child module name + number
    // e.g., "Contacts5" starts with "Contacts"
    if (matches.length === 0) {
      const pattern = new RegExp(`^${childModuleApiName}\\d*$`)
      matches = relatedLists.filter((rl: any) => pattern.test(rl.api_name))
    }

    console.log(
      `Found ${matches.length} related list(s) for ${childModuleApiName} on ${parentModule}:`,
      matches.map((m: any) => ({
        api_name: m.api_name,
        name: m.name,
        type: m.type,
        module_api_name: m.module?.api_name,
      })),
    )

    if (matches.length === 0) {
      console.warn(
        `No related list found for ${childModuleApiName} on ${parentModule}.`,
      )
      // Log all available for debugging
      console.log(
        'All available related lists:',
        relatedLists.map((rl: any) => ({
          api_name: rl.api_name,
          name: rl.name,
          module: rl.module?.api_name,
        })),
      )
      relatedListNameCache[cacheKey] = childModuleApiName
      return childModuleApiName
    }

    // Pick the best match:
    // - Prefer "multiselectlookup" type
    // - Prefer names ending with a number (custom relation API names like "Contacts5")
    // - Prefer plural names
    let bestMatch = matches[0]

    for (const match of matches) {
      if (match.type === 'multiselectlookup') {
        bestMatch = match
        break
      }
      if (/\d+$/.test(match.api_name) && !/\d+$/.test(bestMatch.api_name)) {
        bestMatch = match
      }
    }

    const apiName = bestMatch.api_name
    console.log(
      `Using related list API name: "${apiName}" (type: ${bestMatch.type}, name: "${bestMatch.name}")`,
    )

    relatedListNameCache[cacheKey] = apiName
    return apiName
  } catch (err) {
    console.error(
      `Failed to discover related list name for ${parentModule} → ${childModuleApiName}:`,
      err,
    )
    relatedListNameCache[cacheKey] = childModuleApiName
    return childModuleApiName
  }
}

/**
 * Fetch ALL related record IDs for a parent record, paginating through all pages.
 * Only extracts IDs — does NOT return full contact data.
 *
 * Uses v8 Related Records API:
 * GET /crm/v8/{module_api_name}/{record_id}/{related_list_api_name}
 *
 * IMPORTANT: For multiselect lookup relations, the API returns JUNCTION records.
 * This function extracts the actual child record IDs from the junction's lookup field.
 */
async function fetchRelatedContactIds(
  parentModule: string,
  parentId: string,
  childModule: string,
  onProgress?: (count: number) => void,
): Promise<string[]> {
  // 1. Use hardcoded name directly if available (most reliable)
  const key = `${parentModule}:${childModule}`
  if (RELATED_LIST_NAMES[key]) {
    const apiName = RELATED_LIST_NAMES[key]
    console.log(
      `Using hardcoded related list name: "${apiName}" for ${parentModule}/${parentId}`,
    )
    try {
      const ids = await fetchRelatedIdsWithName(
        parentModule,
        parentId,
        apiName,
        onProgress,
        childModule,
      )
      console.log(
        `✓ Found ${ids.length} related IDs using hardcoded "${apiName}"`,
      )
      return ids
    } catch (err) {
      console.warn(
        `✗ Hardcoded "${apiName}" failed:`,
        err instanceof Error ? err.message : err,
      )
    }
  }

  // 2. Try auto-discovery via v8 Settings API
  try {
    const discoveredName = await discoverRelatedListName(
      parentModule,
      childModule,
    )
    console.log(`Trying auto-discovered name: "${discoveredName}"`)
    const ids = await fetchRelatedIdsWithName(
      parentModule,
      parentId,
      discoveredName,
      onProgress,
      childModule,
    )
    if (ids.length > 0) {
      console.log(
        `✓ Found ${ids.length} related IDs using discovered "${discoveredName}"`,
      )
      return ids
    }
  } catch (err) {
    console.warn(
      'Auto-discovery failed:',
      err instanceof Error ? err.message : err,
    )
  }

  console.log(`No related contacts found for ${parentModule}/${parentId}`)
  return []
}

async function fetchRelatedIdsWithName(
  parentModule: string,
  parentId: string,
  relatedListApiName: string,
  onProgress?: (count: number) => void,
  childModuleName?: string,
): Promise<string[]> {
  const allIds: string[] = []
  let page = 1
  const perPage = 200

  // The Related Records API requires a `fields` parameter (mandatory in v8).
  // For junction records (multiselect lookups), we need the child module lookup field
  // to extract the actual child record ID.
  // e.g., for Mailing_Lists/{id}/Contacts5, the junction has a "Contacts" lookup field.
  const fieldsParam = childModuleName
    ? `fields=${childModuleName},Email,Name,Owner`
    : 'fields=id,Name,Email,Owner'

  while (true) {
    try {
      // Use v8 API for Related Records
      // Pattern: crm/v8/{module_api_name}/{record_id}/{related_list_api_name}?fields=...
      // The `fields` parameter is MANDATORY per Zoho v8 docs.
      const url = `crm/${CRM_VERSION}/${parentModule}/${parentId}/${relatedListApiName}?${fieldsParam}&page=${page}&per_page=${perPage}`
      console.log(`Fetching related IDs: ${url}`)

      const response = await callZohoAPI(url)
      const records = handleCrmListResponse<any>(response)

      // Log first response for debugging
      if (page === 1) {
        console.log(`  Response: ${records.length} records`)
        if (records.length > 0) {
          console.log('  Keys:', Object.keys(records[0]))
          console.log(
            '  First record:',
            JSON.stringify(records[0]).substring(0, 500),
          )
        }
      }

      // Extract the ACTUAL child record IDs from junction records.
      // The junction record has a lookup field named after the child module
      // (e.g., "Contacts" → {name, id}) containing the real contact ID.
      for (const record of records) {
        let childId: string | null = null

        // Strategy 1: Look for the child module lookup field by name
        // e.g., record.Contacts = {name: "...", id: "actual_id"}
        if (childModuleName) {
          const lookupField = record[childModuleName]
          if (
            lookupField &&
            typeof lookupField === 'object' &&
            lookupField.id
          ) {
            childId = String(lookupField.id)
          }
        }

        // Strategy 2: Auto-detect — scan all fields for a lookup object
        // that looks like {name, id} and isn't the parent module
        if (!childId) {
          for (const [key, value] of Object.entries(record)) {
            if (
              key === 'Owner' ||
              key === 'Created_By' ||
              key === 'Modified_By' ||
              key.startsWith('$')
            )
              continue
            if (
              value &&
              typeof value === 'object' &&
              !Array.isArray(value) &&
              (value as any).id &&
              (value as any).name
            ) {
              // Skip if this is the parent module's lookup field
              const parentModuleSingular = parentModule
                .replace(/_/g, ' ')
                .replace(/s$/, '')
              const keyNormalized = key.replace(/_/g, ' ')
              if (
                keyNormalized
                  .toLowerCase()
                  .includes(parentModuleSingular.toLowerCase()) ||
                key === 'Mailing_List' ||
                key === parentModule
              )
                continue

              // This looks like the child record lookup
              childId = String((value as any).id)
              if (page === 1 && allIds.length === 0) {
                console.log(
                  `  Auto-detected child lookup field: "${key}" → id: ${childId}`,
                )
              }
              break
            }
          }
        }

        // Strategy 3: Fallback to record.id (for non-junction related lists like Notes)
        if (!childId && record.id) {
          childId = String(record.id)
          if (page === 1 && allIds.length === 0) {
            console.log(
              `  Using record.id as fallback (non-junction related list)`,
            )
          }
        }

        if (childId) {
          allIds.push(childId)
        }
      }

      if (onProgress) onProgress(allIds.length)

      if (!hasMoreRecords(response) || records.length < perPage) {
        break
      }
      page++
    } catch (err) {
      if (isNoDataError(err)) break
      throw err
    }
  }

  return allIds
}

// Keep the old function signature for backward compatibility (used by mailingListsApi.getContacts)
async function fetchAllRelatedContacts(
  parentModule: string,
  parentId: string,
  childModule: string,
  onProgress?: (count: number) => void,
): Promise<CrmContact[]> {
  // Get IDs first using v8 Related Records API
  const ids = await fetchRelatedContactIds(
    parentModule,
    parentId,
    childModule,
    onProgress,
  )
  if (ids.length === 0) return []

  // Fetch full contact data in batches
  return hydrateContactsByIds(ids, onProgress)
}

/**
 * Fetch full contact records by their IDs, in batches of up to 100.
 * Uses GET /crm/v2/Contacts?ids=id1,id2,...&fields=...
 */
async function hydrateContactsByIds(
  ids: string[],
  onProgress?: (count: number) => void,
): Promise<CrmContact[]> {
  const allContacts: CrmContact[] = []
  const batchSize = 100 // Zoho allows up to 100 IDs per request

  for (let i = 0; i < ids.length; i += batchSize) {
    const batch = ids.slice(i, i + batchSize)
    const idsParam = batch.join(',')

    try {
      const url = `${CONTACTS_MODULE}?ids=${idsParam}&fields=${CONTACT_FIELDS}`
      console.log(
        `Hydrating contacts batch ${Math.floor(i / batchSize) + 1}: ${batch.length} IDs`,
      )
      const response = await callZohoAPI(url)
      const contacts = handleCrmListResponse<CrmContact>(response)
      allContacts.push(...contacts)
    } catch (err) {
      if (!isNoDataError(err)) {
        console.warn(`Failed to hydrate batch starting at index ${i}:`, err)
      }
    }

    if (onProgress) onProgress(allContacts.length)
  }

  console.log(
    `Hydrated ${allContacts.length} / ${ids.length} contacts with full data`,
  )
  return allContacts
}

// ── Tags CRUD ──

const TAGS_MODULE = 'crm/v2/Tags'

export const crmTagsApi = {
  list: async (): Promise<CrmTag[]> => {
    try {
      const response = await callZohoAPI(TAGS_MODULE)
      return handleCrmListResponse<CrmTag>(response)
    } catch (err) {
      if (isNoDataError(err)) return []
      logAndThrow('List CRM Tags', err)
    }
  },

  get: async (id: string): Promise<CrmTag | null> => {
    try {
      const response = await callZohoAPI(`${TAGS_MODULE}/${id}`)
      if (response?.data?.[0]) return response.data[0]
      return null
    } catch (err) {
      logAndThrow('Get CRM Tag', err, { id })
    }
  },

  create: async (data: { Name: string; Team?: string }): Promise<any> => {
    try {
      return await callZohoAPI(TAGS_MODULE, 'POST', { data: [data] })
    } catch (err) {
      logAndThrow('Create CRM Tag', err, data)
    }
  },

  update: async (
    id: string,
    data: { Name?: string; Team?: string },
  ): Promise<boolean> => {
    try {
      await callZohoAPI(TAGS_MODULE, 'PUT', { data: [{ id, ...data }] })
      return true
    } catch (err) {
      logAndThrow('Update CRM Tag', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<boolean> => {
    try {
      await callZohoAPI(`${TAGS_MODULE}?ids=${id}`, 'DELETE')
      return true
    } catch (err) {
      logAndThrow('Delete CRM Tag', err, { id })
    }
  },
}

// ── Categories CRUD ──

const CATEGORIES_MODULE = 'crm/v2/Categories'

export const crmCategoriesApi = {
  list: async (): Promise<CrmCategory[]> => {
    try {
      const response = await callZohoAPI(CATEGORIES_MODULE)
      return handleCrmListResponse<CrmCategory>(response)
    } catch (err) {
      if (isNoDataError(err)) return []
      logAndThrow('List CRM Categories', err)
    }
  },

  get: async (id: string): Promise<CrmCategory | null> => {
    try {
      const response = await callZohoAPI(`${CATEGORIES_MODULE}/${id}`)
      if (response?.data?.[0]) return response.data[0]
      return null
    } catch (err) {
      logAndThrow('Get CRM Category', err, { id })
    }
  },

  create: async (data: {
    Name: string
    Description?: string
    Team?: string
  }): Promise<any> => {
    try {
      return await callZohoAPI(CATEGORIES_MODULE, 'POST', { data: [data] })
    } catch (err) {
      logAndThrow('Create CRM Category', err, data)
    }
  },

  update: async (
    id: string,
    data: { Name?: string; Description?: string; Team?: string },
  ): Promise<boolean> => {
    try {
      await callZohoAPI(CATEGORIES_MODULE, 'PUT', { data: [{ id, ...data }] })
      return true
    } catch (err) {
      logAndThrow('Update CRM Category', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<boolean> => {
    try {
      await callZohoAPI(`${CATEGORIES_MODULE}?ids=${id}`, 'DELETE')
      return true
    } catch (err) {
      logAndThrow('Delete CRM Category', err, { id })
    }
  },
}

// ── Mailing Lists CRUD ──

const MAILING_LISTS_MODULE = 'crm/v2/Mailing_Lists'

export const crmMailingListsApi = {
  list: async (): Promise<CrmMailingList[]> => {
    try {
      const response = await callZohoAPI(MAILING_LISTS_MODULE)
      return handleCrmListResponse<CrmMailingList>(response)
    } catch (err) {
      if (isNoDataError(err)) return []
      logAndThrow('List Mailing Lists', err)
    }
  },

  get: async (id: string): Promise<CrmMailingList | null> => {
    try {
      const response = await callZohoAPI(`${MAILING_LISTS_MODULE}/${id}`)
      if (response?.data?.[0]) return response.data[0]
      return null
    } catch (err) {
      logAndThrow('Get Mailing List', err, { id })
    }
  },

  create: async (data: Partial<Omit<CrmMailingList, 'id'>>): Promise<any> => {
    try {
      return await callZohoAPI(MAILING_LISTS_MODULE, 'POST', { data: [data] })
    } catch (err) {
      logAndThrow('Create Mailing List', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<CrmMailingList, 'id'>>,
  ): Promise<boolean> => {
    try {
      await callZohoAPI(MAILING_LISTS_MODULE, 'PUT', {
        data: [{ id, ...data }],
      })
      return true
    } catch (err) {
      logAndThrow('Update Mailing List', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<boolean> => {
    try {
      await callZohoAPI(`${MAILING_LISTS_MODULE}?ids=${id}`, 'DELETE')
      return true
    } catch (err) {
      logAndThrow('Delete Mailing List', err, { id })
    }
  },

  /**
   * Get all contacts associated with a specific mailing list.
   * Auto-discovers the correct related list API name via Settings API.
   */
  getContacts: async (
    mailingListId: string,
    onProgress?: (count: number) => void,
  ): Promise<CrmContact[]> => {
    return fetchAllRelatedContacts(
      'Mailing_Lists',
      mailingListId,
      'Contacts',
      onProgress,
    )
  },
}

// ── Contacts CRUD ──

const CONTACTS_MODULE = 'crm/v2/Contacts'

export interface AudienceSearchCriteria {
  regions?: string[]
  mailingListIds?: string[]
  mailingListNames?: string[]
  tagIds?: string[]
  tagNames?: string[]
  categoryIds?: string[]
  categoryNames?: string[]
  team?: string
}

// Export the discovery function so ApiTest can use it
export { discoverRelatedListName }

export const crmContactsApi = {
  list: async (
    page: number = 1,
    perPage: number = 200,
  ): Promise<CrmContact[]> => {
    try {
      const response = await callZohoAPI(
        `${CONTACTS_MODULE}?fields=${CONTACT_FIELDS}&page=${page}&per_page=${perPage}`,
      )
      return handleCrmListResponse<CrmContact>(response)
    } catch (err) {
      if (isNoDataError(err)) return []
      logAndThrow('List CRM Contacts', err)
    }
  },

  listAll: async (
    onProgress?: (fetched: number) => void,
  ): Promise<CrmContact[]> => {
    const allContacts: CrmContact[] = []
    let page = 1
    const perPage = 200

    while (true) {
      try {
        // Fetch WITHOUT fields parameter to get ALL fields including
        // multiselect lookup fields (Mailing_List, Categories) which
        // cannot be fetched via the fields parameter.
        const response = await callZohoAPI(
          `${CONTACTS_MODULE}?page=${page}&per_page=${perPage}`,
        )
        const rawContacts = handleCrmListResponse<any>(response)
        const contacts = rawContacts.map(normalizeContact)
        allContacts.push(...contacts)

        if (onProgress) onProgress(allContacts.length)

        if (!hasMoreRecords(response) || rawContacts.length < perPage) {
          break
        }
        page++
      } catch (err) {
        if (isNoDataError(err)) break
        throw err
      }
    }

    return allContacts
  },

  get: async (id: string): Promise<CrmContact | null> => {
    try {
      const response = await callZohoAPI(`${CONTACTS_MODULE}/${id}`)
      if (response?.data?.[0]) return response.data[0]
      return null
    } catch (err) {
      logAndThrow('Get CRM Contact', err, { id })
    }
  },

  getRelatedRecords: async (
    contactId: string,
    relatedListName: string,
  ): Promise<any[]> => {
    try {
      const response = await callZohoAPI(
        `${CONTACTS_MODULE}/${contactId}/${relatedListName}`,
      )
      return handleCrmListResponse<any>(response)
    } catch (err) {
      if (isNoDataError(err)) return []
      throw err
    }
  },

  /**
   * Search for contacts matching audience criteria (OR logic).
   *
   * Strategy (robust, multi-layered):
   * 1. Fetch ALL contacts once (without fields restriction to get multiselect lookup data).
   * 2. For MAILING LISTS → Primary: client-side filter on Mailing_List field.
   *    Fallback: COQL search, then Related Records API.
   * 3. For CATEGORIES → Same multi-layered approach.
   * 4. For TAGS → filter the full contacts list client-side (already worked).
   * 5. For REGIONS → filter the full contacts list client-side (already worked).
   * 6. Merge all with OR logic, deduplicate by contact ID.
   */
  searchForAudience: async (
    criteria: AudienceSearchCriteria,
    onProgress?: (message: string) => void,
  ): Promise<CrmContact[]> => {
    const contactMap = new Map<string, CrmContact>()

    try {
      // ── Step 1: Fetch ALL contacts once (single source of truth) ──
      if (onProgress) onProgress('Loading all contacts...')

      const allContacts = await crmContactsApi.listAll((count) => {
        if (onProgress) onProgress(`Loading contacts: ${count} fetched...`)
      })

      if (onProgress)
        onProgress(
          `Loaded ${allContacts.length} contacts. Building audience...`,
        )

      // Build a lookup map: contact ID → full contact record
      const contactById = new Map<string, CrmContact>()
      for (const c of allContacts) {
        if (c.id) {
          contactById.set(String(c.id), c)
        }
      }

      console.log(`Loaded ${allContacts.length} contacts into lookup map`)
      if (allContacts.length > 0) {
        console.log('Sample contact:', {
          id: allContacts[0].id,
          Email: allContacts[0].Email,
          Tags: allContacts[0].Tags,
          Mailing_List: (allContacts[0] as any).Mailing_List,
          Categories: (allContacts[0] as any).Categories,
          Team: allContacts[0].Team,
          keys: Object.keys(allContacts[0]),
        })
      }

      const addContact = (contact: CrmContact) => {
        if (!contact.id) return
        // Team filter
        if (criteria.team && contact.Team && contact.Team !== criteria.team)
          return
        if (!contactMap.has(contact.id)) {
          contactMap.set(contact.id, contact)
        }
      }

      // ── Step 2: MAILING LISTS ──
      const hasMailingListCriteria =
        criteria.mailingListIds && criteria.mailingListIds.length > 0

      if (hasMailingListCriteria) {
        const mailingListNames = (criteria.mailingListNames || []).filter(
          Boolean,
        )

        // ── Strategy A: Client-side filter on Mailing_List field ──
        // This is fast but unreliable — Zoho doesn't always return the
        // multiselect lookup field for every contact in bulk fetches.
        if (mailingListNames.length > 0) {
          if (onProgress) onProgress('Filtering contacts by mailing list...')

          const listMatches = allContacts.filter((contact: any) => {
            if (criteria.team && contact.Team && contact.Team !== criteria.team)
              return false
            if (!contact.Mailing_List) return false
            const contactLists = contact.Mailing_List.split(',')
              .map((s: string) => s.trim())
              .filter(Boolean)
            return contactLists.some((list: string) =>
              mailingListNames.some(
                (name) => list.toLowerCase() === name.toLowerCase(),
              ),
            )
          })

          console.log(
            `Client-side mailing list filter: ${listMatches.length} contacts match [${mailingListNames.join(', ')}]`,
          )
          if (listMatches.length > 0) {
            if (onProgress)
              onProgress(
                `Found ${listMatches.length} contacts in mailing lists (client-side)`,
              )
            for (const c of listMatches) addContact(c)
          }
        }

        // ── Strategy B: Related Records API (ALWAYS run to catch contacts ──
        // whose Mailing_List field wasn't populated in the bulk fetch).
        // This is the authoritative source — it queries the junction table directly.
        // NOTE: We bypass the team filter here because the mailing list association
        // IS the authority. If a contact is linked to a selected mailing list,
        // it belongs in the audience regardless of its Team field value.
        const beforeRelatedCount = contactMap.size
        for (const listId of criteria.mailingListIds!) {
          const listName =
            criteria.mailingListNames?.find(
              (_, i) => criteria.mailingListIds![i] === listId,
            ) || listId

          if (onProgress)
            onProgress(
              `Verifying mailing list contacts: ${listName} (Related Records API)...`,
            )

          try {
            const relatedIds = await fetchRelatedContactIds(
              'Mailing_Lists',
              listId,
              'Contacts',
              (count) => {
                if (onProgress)
                  onProgress(
                    `Mailing list "${listName}": found ${count} IDs...`,
                  )
              },
            )

            console.log(
              `Mailing list "${listName}" (${listId}): ${relatedIds.length} related IDs from Related Records API`,
            )

            let matched = 0
            let newlyAdded = 0
            for (const relId of relatedIds) {
              const contact = contactById.get(relId)
              if (contact) {
                // Add directly to contactMap — bypass team filter for Related Records
                // The mailing list association is the authoritative source
                if (!contactMap.has(contact.id)) {
                  contactMap.set(contact.id, contact)
                  newlyAdded++
                }
                matched++
              }
            }
            console.log(
              `Mailing list "${listName}": ${matched}/${relatedIds.length} IDs matched to contacts, ${newlyAdded} newly added`,
            )

            // If some IDs didn't match our bulk-fetched contacts, fetch them individually
            // Check against contactMap (not contactById) so team-filtered contacts
            // and contacts missing from bulk fetch are both caught
            const unmatchedIds = relatedIds.filter(
              (relId) => !contactById.has(relId) && !contactMap.has(relId),
            )
            if (unmatchedIds.length > 0) {
              console.log(
                `Fetching ${unmatchedIds.length} unmatched contact(s) individually...`,
              )
              if (onProgress)
                onProgress(
                  `Mailing list "${listName}": fetching ${unmatchedIds.length} additional contact(s)...`,
                )

              const hydratedContacts = await hydrateContactsByIds(unmatchedIds)
              for (const contact of hydratedContacts) {
                const normalized = normalizeContact(contact)
                // Add directly — bypass team filter for Related Records results
                if (!contactMap.has(normalized.id)) {
                  contactMap.set(normalized.id, normalized)
                }
                contactById.set(String(normalized.id), normalized)
              }
            }
          } catch (err) {
            console.warn(
              `Failed to fetch contacts for mailing list "${listName}" (${listId}):`,
              err,
            )
          }
        }

        const afterRelatedCount = contactMap.size
        if (afterRelatedCount > beforeRelatedCount) {
          console.log(
            `Related Records API added ${afterRelatedCount - beforeRelatedCount} contact(s) missed by client-side filter`,
          )
        }
      }

      // ── Step 3: CATEGORIES ──
      const hasCategoryCriteria =
        criteria.categoryIds && criteria.categoryIds.length > 0

      if (hasCategoryCriteria) {
        const categoryNames = (criteria.categoryNames || []).filter(Boolean)

        // ── Strategy A: Client-side filter on Categories field ──
        // Fast but unreliable — Zoho doesn't always return multiselect
        // lookup fields for every contact in bulk fetches.
        if (categoryNames.length > 0) {
          if (onProgress) onProgress('Filtering contacts by category...')

          const catMatches = allContacts.filter((contact: any) => {
            if (criteria.team && contact.Team && contact.Team !== criteria.team)
              return false
            if (!contact.Categories) return false
            const contactCats = contact.Categories.split(',')
              .map((s: string) => s.trim())
              .filter(Boolean)
            return contactCats.some((cat: string) =>
              categoryNames.some(
                (name) => cat.toLowerCase() === name.toLowerCase(),
              ),
            )
          })

          console.log(
            `Client-side category filter: ${catMatches.length} contacts match [${categoryNames.join(', ')}]`,
          )
          if (catMatches.length > 0) {
            for (const c of catMatches) addContact(c)
          }
        }

        // ── Strategy B: Related Records API (ALWAYS run to catch contacts ──
        // whose Categories field wasn't populated in the bulk fetch).
        // NOTE: We bypass the team filter here — category association is authoritative.
        const beforeCatRelatedCount = contactMap.size
        for (const catId of criteria.categoryIds!) {
          const catName =
            criteria.categoryNames?.find(
              (_, i) => criteria.categoryIds![i] === catId,
            ) || catId

          if (onProgress)
            onProgress(
              `Verifying category contacts: ${catName} (Related Records API)...`,
            )

          try {
            const relatedIds = await fetchRelatedContactIds(
              'Categories',
              catId,
              'Contacts',
              (count) => {
                if (onProgress)
                  onProgress(`Category "${catName}": found ${count} IDs...`)
              },
            )

            console.log(
              `Category "${catName}" (${catId}): ${relatedIds.length} related IDs from Related Records API`,
            )

            let matched = 0
            let newlyAdded = 0
            for (const relId of relatedIds) {
              const contact = contactById.get(relId)
              if (contact) {
                // Add directly to contactMap — bypass team filter for Related Records
                if (!contactMap.has(contact.id)) {
                  contactMap.set(contact.id, contact)
                  newlyAdded++
                }
                matched++
              }
            }
            console.log(
              `Category "${catName}": ${matched}/${relatedIds.length} IDs matched to contacts, ${newlyAdded} newly added`,
            )

            // Check against both maps for unmatched IDs
            const unmatchedIds = relatedIds.filter(
              (relId) => !contactById.has(relId) && !contactMap.has(relId),
            )
            if (unmatchedIds.length > 0) {
              console.log(
                `Fetching ${unmatchedIds.length} unmatched contact(s) individually...`,
              )
              if (onProgress)
                onProgress(
                  `Category "${catName}": fetching ${unmatchedIds.length} additional contact(s)...`,
                )

              const hydratedContacts = await hydrateContactsByIds(unmatchedIds)
              for (const contact of hydratedContacts) {
                const normalized = normalizeContact(contact)
                // Add directly — bypass team filter for Related Records results
                if (!contactMap.has(normalized.id)) {
                  contactMap.set(normalized.id, normalized)
                }
                contactById.set(String(normalized.id), normalized)
              }
            }
          } catch (err) {
            console.warn(
              `Failed to fetch contacts for category "${catName}" (${catId}):`,
              err,
            )
          }
        }

        const afterCatRelatedCount = contactMap.size
        if (afterCatRelatedCount > beforeCatRelatedCount) {
          console.log(
            `Related Records API added ${afterCatRelatedCount - beforeCatRelatedCount} category contact(s) missed by client-side filter`,
          )
        }
      }

      // ── Step 4: TAGS (client-side filter on full contacts) ──
      const hasTagCriteria = criteria.tagNames && criteria.tagNames.length > 0

      if (hasTagCriteria) {
        const tagMatches = allContacts.filter((contact) => {
          if (criteria.team && contact.Team && contact.Team !== criteria.team)
            return false
          if (!contact.Tags) return false
          const contactTags = contact.Tags.split(',')
            .map((s) => s.trim())
            .filter(Boolean)
          return contactTags.some((tag) =>
            criteria.tagNames!.some(
              (name) => tag.toLowerCase() === name.toLowerCase(),
            ),
          )
        })
        console.log(
          `Tag filter: ${tagMatches.length} contacts match tags: ${criteria.tagNames!.join(', ')}`,
        )
        for (const c of tagMatches) addContact(c)
      }

      // ── Step 5: REGIONS (client-side filter on full contacts) ──
      const hasRegionCriteria = criteria.regions && criteria.regions.length > 0

      if (hasRegionCriteria) {
        const regionMatches = allContacts.filter((contact) => {
          if (criteria.team && contact.Team && contact.Team !== criteria.team)
            return false
          return (
            contact.Sub_Region != null &&
            criteria.regions!.includes(contact.Sub_Region)
          )
        })
        console.log(
          `Region filter: ${regionMatches.length} contacts match regions: ${criteria.regions!.join(', ')}`,
        )
        for (const c of regionMatches) addContact(c)
      }

      const results = Array.from(contactMap.values())

      if (onProgress)
        onProgress(
          `Found ${results.length} unique contacts (deduplicated by ID)`,
        )
      console.log(
        `Audience search complete: ${results.length} unique contacts`,
        { criteria },
      )

      return results
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error'
      console.error('Audience search failed:', errorMsg, (err as any)?.debug)
      const detailedErr = new Error(`Audience search failed: ${errorMsg}`)
      ;(detailedErr as any).debug = (err as any)?.debug
      throw detailedErr
    }
  },

  assignBucket: async (
    contactIds: string[],
    bucketId: string,
    expiresAt?: string,
    onProgress?: (progress: BucketAssignProgress) => void,
  ): Promise<{
    success: boolean
    updatedCount: number
    failedCount: number
    errors: string[]
  }> => {
    try {
      if (contactIds.length === 0) {
        return { success: true, updatedCount: 0, failedCount: 0, errors: [] }
      }

      const batchSize = 100
      const totalBatches = Math.ceil(contactIds.length / batchSize)
      let updatedCount = 0
      let failedCount = 0
      const errors: string[] = []

      for (let i = 0; i < contactIds.length; i += batchSize) {
        const batch = contactIds.slice(i, i + batchSize)
        const currentBatch = Math.floor(i / batchSize) + 1

        // Only send the fields we need to update — Bucket_ID and optionally Bucket_ID_Expires_At.
        // Per Zoho CRM API docs, only include fields that should be changed.
        const updateData = batch.map((id) => {
          const record: Record<string, any> = {
            id,
            Bucket_ID: bucketId,
          }
          // Only include Bucket_ID_Expires_At if a value is provided
          if (expiresAt) {
            record.Bucket_ID_Expires_At = expiresAt
          }
          return record
        })

        const requestPayload = { data: updateData }

        try {
          const response = await callZohoAPI(
            CONTACTS_MODULE,
            'PUT',
            requestPayload,
          )

          // Log the full raw response for debugging
          console.log(
            `Batch ${currentBatch} raw response:`,
            JSON.stringify(response).substring(0, 2000),
          )

          if (response?.data) {
            const batchSuccess = response.data.filter(
              (r: any) => r.status === 'success',
            ).length
            const batchFailed = response.data.filter(
              (r: any) => r.status !== 'success',
            )
            updatedCount += batchSuccess
            failedCount += batchFailed.length

            // Log each failed record with FULL Zoho error details
            batchFailed.forEach((r: any) => {
              const errorDetail = [
                r.code && `code: ${r.code}`,
                r.message && `message: ${r.message}`,
                r.details && `details: ${JSON.stringify(r.details)}`,
                r.status && `status: ${r.status}`,
              ]
                .filter(Boolean)
                .join(', ')

              const errorMsg =
                errorDetail || `Failed to update contact (unknown error)`
              errors.push(errorMsg)

              // Log each failure to Error Log with full context
              errorLogService.log('Bucket Assign — Record Failed', errorMsg, {
                contactId: r.details?.id || 'unknown',
                zohoResponse: r,
                sentPayload:
                  updateData.find((d) => d.id === r.details?.id) ||
                  updateData[0],
                batchNumber: currentBatch,
                apiUrl: CONTACTS_MODULE,
                method: 'PUT',
              })
            })
          } else {
            // No data array in response — log the entire response
            errorLogService.log(
              'Bucket Assign — Unexpected Response',
              'Response has no data array',
              {
                rawResponse: response,
                sentPayload: requestPayload,
                batchNumber: currentBatch,
                apiUrl: CONTACTS_MODULE,
                method: 'PUT',
              },
            )
            updatedCount += batch.length
          }
        } catch (batchErr) {
          failedCount += batch.length
          const errMsg =
            batchErr instanceof Error ? batchErr.message : 'Unknown error'
          const debugInfo = (batchErr as any)?.debug
          errors.push(`Batch ${currentBatch} failed: ${errMsg}`)

          // Log batch failure to Error Log with full debug info
          errorLogService.log('Bucket Assign — Batch Failed', errMsg, {
            batchNumber: currentBatch,
            contactIds: batch,
            sentPayload: requestPayload,
            apiUrl: CONTACTS_MODULE,
            method: 'PUT',
            debug: debugInfo,
          })
        }

        if (onProgress) {
          onProgress({
            totalContacts: contactIds.length,
            processedContacts: Math.min(i + batchSize, contactIds.length),
            currentBatch,
            totalBatches,
            updatedCount,
            failedCount,
          })
        }
      }

      return { success: errors.length === 0, updatedCount, failedCount, errors }
    } catch (err) {
      logAndThrow('Assign Bucket to Contacts', err, {
        contactCount: contactIds.length,
        bucketId,
      })
    }
  },

  clearBucket: async (
    bucketId: string,
    onProgress?: (progress: BucketAssignProgress) => void,
  ): Promise<{ success: boolean; clearedCount: number; errors: string[] }> => {
    try {
      const query = `select id from Contacts where (Bucket_ID:equals:${bucketId})`
      const response = await callZohoAPI('crm/v2/coql', 'POST', {
        select_query: query,
      })
      const contacts = handleCrmListResponse<{ id: string }>(response)
      if (contacts.length === 0)
        return { success: true, clearedCount: 0, errors: [] }

      const result = await crmContactsApi.assignBucket(
        contacts.map((c) => c.id),
        '',
        undefined,
        onProgress,
      )
      return {
        success: result.success,
        clearedCount: result.updatedCount,
        errors: result.errors,
      }
    } catch (err) {
      if (isNoDataError(err))
        return { success: true, clearedCount: 0, errors: [] }
      return {
        success: false,
        clearedCount: 0,
        errors: [err instanceof Error ? err.message : 'Unknown error'],
      }
    }
  },

  clearExpiredBuckets: async (
    onProgress?: (progress: BucketAssignProgress) => void,
  ): Promise<{ success: boolean; clearedCount: number; errors: string[] }> => {
    try {
      const now = formatZohoDateTime(new Date())
      const query = `select id from Contacts where (Bucket_ID_Expires_At:before:${now}) and (Bucket_ID:is_not:null)`
      const response = await callZohoAPI('crm/v2/coql', 'POST', {
        select_query: query,
      })
      const contacts = handleCrmListResponse<{ id: string }>(response)
      if (contacts.length === 0)
        return { success: true, clearedCount: 0, errors: [] }

      const result = await crmContactsApi.assignBucket(
        contacts.map((c) => c.id),
        '',
        undefined,
        onProgress,
      )
      return {
        success: result.success,
        clearedCount: result.updatedCount,
        errors: result.errors,
      }
    } catch (err) {
      if (isNoDataError(err))
        return { success: true, clearedCount: 0, errors: [] }
      return {
        success: false,
        clearedCount: 0,
        errors: [err instanceof Error ? err.message : 'Unknown error'],
      }
    }
  },
}
